import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import API from "@src/API.js";
import BreadCrumb from "../Controls/BreadCrumb";
import CustomButton from "../Controls/CustomButton";
import UserData from "../Controls/UserData";
import { toast } from "react-toastify";

const LEVEL_NAMES = {
  1: "Beginner",
  2: "Junior",
  3: "Intermediate",
  4: "Senior",
  5: "Guru",
};

export default function SkillTests() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const userData = UserData.UserData() || {};
  const personSkills = userData.personSkills || [];
  const [skillId, setSkillId] = useState(() => Number(params.get("skillId")) || personSkills[0]?.SkillID || 0);
  const [eligible, setEligible] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!skillId) return;
    let cancelled = false;
    (async () => {
      const resp = await API.getAsync(`/Skill/Test/Eligible?SkillID=${skillId}`);
      if (!cancelled && resp?.ok) setEligible(resp.jsonData);
    })();
    return () => {
      cancelled = true;
    };
  }, [skillId]);

  async function startTest(mode, skillLevelID) {
    if (busy) return;
    setBusy(true);
    try {
      const resp = await API.postAsync("/Skill/Test/Start", {
        skillID: skillId,
        mode,
        skillLevelID: skillLevelID || 0,
      });
      if (resp?.ok && resp.jsonData?.testId) {
        navigate(`/skillTest/${resp.jsonData.testId}`);
      } else {
        toast.error(resp?.jsonData?.message || "Could not start test");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pagebody">
      <BreadCrumb
        props={[
          { route: "/skills", label: "Skills" },
          { label: "Skill Tests" },
        ]}
      />
      <div className="tabbody">
        <h1>Skill Tests</h1>
        <p>
          Type 1 certifies a level (verified badge). Type 2 estimates your level without setting a verified badge.
        </p>
        <p>
          <Link to={`/skillQuestions${skillId ? `?skillId=${skillId}` : ""}`}>Community questions</Link>
        </p>

        <label>
          Skill{" "}
          <select
            value={skillId || ""}
            onChange={(e) => setSkillId(Number(e.target.value) || 0)}
          >
            <option value="">Select a skill</option>
            {personSkills.map((ps) => (
              <option key={ps.ID} value={ps.SkillID}>
                {ps.SkillName || `Skill ${ps.SkillID}`}
              </option>
            ))}
          </select>
        </label>

        {!skillId && <p>Add a skill on your Skills page first, then return here.</p>}

        {eligible && (
          <div className="skillTestEligible">
            <h2>Eligibility</h2>
            <p>
              Type 2 discover:{" "}
              {eligible.type2Eligible ? (
                <CustomButton type="button" variant="contained" color="primary" disabled={busy} onClick={() => startTest(2, 0)}>
                  Start Type 2
                </CustomButton>
              ) : (
                <span>Need 20 approved questions on this skill</span>
              )}
            </p>
            <ul>
              {(eligible.levels || []).map((lvl) => (
                <li key={lvl.skillLevel}>
                  Type 1 {LEVEL_NAMES[lvl.skillLevel] || lvl.name}: {lvl.approvedCount}/20 approved
                  {lvl.type1Eligible ? (
                    <>
                      {" "}
                      <CustomButton
                        type="button"
                        variant="contained"
                        color="primary"
                        disabled={busy}
                        onClick={() => startTest(1, lvl.skillLevel)}
                      >
                        Start Type 1
                      </CustomButton>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
